import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import type { DataSource, Id } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NothingYet } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import { RankedBarChart } from "@/components/charts/lazy";
import {
  formatCompactMoney,
  formatCount,
  formatRupees,
} from "@/lib/chart-format";
import { formatMoney } from "@/modules/gst/format";
import { fetchAllPages } from "@/modules/reports/lib/pagination";
import { ReportHeader } from "@/modules/reports/ReportHeader";
import { ProductViews } from "@/modules/reports/screens/ProductViews";
import { SkuTrends } from "@/modules/reports/screens/SkuTrends";
import {
  decodeProducts,
  encodeProducts,
  type ProductsState,
} from "@/modules/reports/skuTrends";
import { useSectionSearch, type SectionSearch } from "@/routes/search";
import {
  aggregateProductSales,
  type ProductSalesRow,
  type VariantLabel,
  topByQuantity,
  topByRevenue,
} from "@/modules/reports/products";

const RANKED_ROWS = 10;

/**
 * Every line item across every order (ticket #388).
 *
 * Reads `orders.listLineItems` — one join, paged — rather than `orders.list`
 * followed by `orders.get` once per order (#161's `mapWithConcurrency`,
 * removed with this change: nothing else used it). Against nonprod-shaped
 * data the old path either failed outright (every order's `.get()` fired at
 * once exhausts the database pool) or took 52-67s paced through in batches
 * of 20; the bulk read is the fix #161 filed as a follow-up rather than
 * building, since it needed a new `OrdersRepository` method on both
 * adapters plus a new API route.
 *
 * No catalog read here at all, on purpose: this used to fetch every
 * product's detail (`catalog.getProduct`, one call per product) purely to
 * build a `variantId → {productTitle, variantTitle}` label, which measured
 * over 12s for ~100 products against nonprod-shaped data — squarely past the
 * ticket's 2s bar. `aggregateProductSales` already falls back to the line
 * item's own `title` and `sku` for any variant it has no label for, and
 * every line item this app writes already carries a Shopify-style combined
 * "Product — Variant" title, captured at the time of sale — so an empty
 * label map costs nothing but the Product/Variant split rendering as one
 * column instead of two.
 */
async function loadLineItemSales(data: DataSource): Promise<ProductSalesRow[]> {
  const lineItems = await fetchAllPages((query) =>
    data.orders.listLineItems(query),
  );

  return aggregateProductSales(lineItems, new Map<Id, VariantLabel>());
}

function productName(row: ProductSalesRow): string {
  return row.variantTitle
    ? `${row.productTitle} · ${row.variantTitle}`
    : row.productTitle;
}

function RankedChart({
  title,
  description,
  rows,
  valueLabel,
  valueOf,
  amountOf,
  formatAmount,
  formatExact,
}: {
  title: string;
  description: string;
  rows: ProductSalesRow[];
  valueLabel: string;
  /** The figure as the table prints it, exact. */
  valueOf: (row: ProductSalesRow) => string;
  /** The same figure as a number, for the length of the bar. */
  amountOf: (row: ProductSalesRow) => number;
  formatAmount: (value: number) => string;
  /** The figure in the tooltip, exact. */
  formatExact?: (value: number) => string;
}) {
  return (
    <ChartCard
      title={title}
      description={description}
      table={
        <FigureTable
          rows={rows}
          rowKey={(row) => row.key}
          columns={[
            { header: "#", cell: (_, index) => index + 1 },
            {
              header: "Product",
              cell: (row) => (
                <>
                  {row.productTitle}
                  {row.variantTitle ? (
                    <span className="text-muted-foreground block text-xs">
                      {row.variantTitle}
                    </span>
                  ) : null}
                </>
              ),
            },
            {
              header: "SKU",
              cell: (row) => (
                <span className="text-muted-foreground">{row.sku ?? "—"}</span>
              ),
            },
            { header: valueLabel, numeric: true, strong: true, cell: valueOf },
          ]}
        />
      }
    >
      <RankedBarChart
        rows={rows.map((row) => ({
          key: row.key,
          label: row.productTitle,
          detail: row.variantTitle || undefined,
          value: amountOf(row),
          note: row.sku ? `SKU ${row.sku}` : undefined,
        }))}
        valueLabel={valueLabel}
        formatValue={formatAmount}
        formatExact={formatExact}
        ariaLabel={`${title}: ${rows
          .map((row) => `${productName(row)} ${valueOf(row)}`)
          .join(", ")}.`}
      />
    </ChartCard>
  );
}

/**
 * Reports > Products: the best sellers it opened with, and SKU trends over a
 * range of dates (#678). One page with a switch, so the view is in the URL and
 * a link opens the same one.
 */
export function Products() {
  const navigate = useNavigate();
  const urlSearch = useSectionSearch();
  const state = useMemo(
    () => decodeProducts(urlSearch.chart),
    [urlSearch.chart],
  );

  function change(patch: Partial<ProductsState>) {
    void navigate({
      to: ".",
      search: (prev: SectionSearch) => ({
        ...prev,
        chart: encodeProducts({ ...state, ...patch }),
      }),
    });
  }

  return state.view === "trends" ? (
    <SkuTrends state={state} change={change} />
  ) : (
    <BestSellers state={state} change={change} />
  );
}

/**
 * Best sellers by quantity and by value, derived from order line items
 * (FR-DSH-3, ticket #96).
 *
 * The two rankings routinely disagree: a ₹199 accessory sold 40 times can
 * out-rank a ₹4,999 piece sold three times on quantity while losing on
 * value, and reordering stock off only one of these two lists is how a shop
 * ends up overstocked on what is cheap to buy and out of what actually pays
 * for the month.
 */
function BestSellers({
  state,
  change,
}: {
  state: ProductsState;
  change: (patch: Partial<ProductsState>) => void;
}) {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["reports", "products"],
    queryFn: () => loadLineItemSales(data),
  });

  return (
    <div className="flex flex-col gap-6">
      <ReportHeader readAt={query.data ? query.dataUpdatedAt : undefined} />
      <ProductViews state={state} change={change} />
      <ProductsBody query={query} />
    </div>
  );
}

function ProductsBody({ query }: { query: UseQueryResult<ProductSalesRow[]> }) {
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (query.isPending) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <TableSkeleton />
        <TableSkeleton />
      </div>
    );
  }

  if (query.data.length === 0) {
    return (
      <NothingYet
        title="No line items yet"
        description="Best sellers appear once orders have been placed."
      />
    );
  }

  const byQuantity = topByQuantity(query.data, RANKED_ROWS);
  const byRevenue = topByRevenue(query.data, RANKED_ROWS);

  const scope = `Top ${String(Math.min(RANKED_ROWS, query.data.length))} of ${String(query.data.length)} variants sold, across every order.`;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <RankedChart
        title="Best sellers by quantity"
        description={`${scope} Ranked by units sold — what moves fastest.`}
        rows={byQuantity}
        valueLabel="Units"
        valueOf={(row) => String(row.quantity)}
        amountOf={(row) => row.quantity}
        formatAmount={formatCount}
      />
      <RankedChart
        title="Best sellers by value"
        description={`${scope} Ranked by revenue — what pays for the month.`}
        rows={byRevenue}
        valueLabel="Revenue"
        valueOf={(row) => formatMoney(row.revenue)}
        amountOf={(row) => Number(row.revenue)}
        formatAmount={formatCompactMoney}
        formatExact={formatRupees}
      />
    </div>
  );
}
